/**
 * The app's HTTP client: the canonical vendored factory, configured for this
 * console's two bases.
 *
 *  - saas-auth (the bootstrap `authOrigin`) — identity + the cookie-bearing
 *    auth surface. Credentialed; CSRF double-submit applies.
 *  - dink-api (derived via `apiBase()`) — operations. Bearer-only,
 *    UNcredentialed, no CSRF.
 *
 * Tokens are in memory only. Interceptor logic lives in
 * `./httpClientFactory` (vendored, never edited) — this module supplies config.
 */
import { createApiClient } from './httpClientFactory';
import { apiBase } from '@/config/platform';
import { getBootstrap } from '@/config/bootstrap';
import {
  getAccessToken,
  getCsrfToken,
  setAccessToken,
  setCsrfToken,
  signOut,
} from '@/auth/authStore';
import { generateRequestId } from '@/utils/uuid';

/**
 * The dink-api operational surface. `resolveBaseUrl` and `sendCredentials` MUST
 * agree on this prefix: a path routed to dink-api but not recognised by the
 * credential predicate would be sent credentialed and fail CORS preflight. One
 * constant so they cannot drift.
 *
 * ASSUMPTION (dink-api resource-server acceptance is held — handoff P6): the
 * operational path prefix mirrors the platform convention. Confirm against the
 * dink-api contract when the first operational page is specced; this constant
 * is the single change point, and the predicate tests below pin it.
 */
export const DINK_API_PREFIX = '/admin/platform/';

const CSRF_PROTECTED_EXACT: ReadonlySet<string> = new Set([
  '/api/auth/refresh',
  '/api/auth/logout',
]);
const CSRF_PROTECTED_PREFIXES: readonly string[] = ['/api/account/', '/api/product-admin/'];

export function isDinkApiPath(path: string): boolean {
  return path.startsWith(DINK_API_PREFIX);
}

export function resolveBaseUrl(path: string): string {
  return isDinkApiPath(path) ? apiBase() : getBootstrap().authOrigin;
}

/**
 * Per-base credential posture. Cookies travel to the auth origin only — the
 * refresh cookie is HttpOnly and ambient there. dink-api authenticates by
 * bearer token alone, has no use for cookies, and does not emit
 * `Access-Control-Allow-Credentials`; sending credentials to it makes the
 * browser demand that header and fails every preflight.
 */
export function sendCredentials(path: string): boolean {
  return !isDinkApiPath(path);
}

export function isBearerPath(path: string): boolean {
  return (
    isDinkApiPath(path) ||
    path.startsWith('/api/account/') ||
    path.startsWith('/api/product-admin/')
  );
}

/** CSRF applies to the auth origin ONLY. dink-api writes are bearer-only. */
export function isCsrfProtected(path: string): boolean {
  if (isDinkApiPath(path)) return false;
  if (CSRF_PROTECTED_EXACT.has(path)) return true;
  return CSRF_PROTECTED_PREFIXES.some((p) => path.startsWith(p));
}

export const api = createApiClient({
  resolveBaseUrl,
  sendCredentials,
  tokens: {
    getAccess: getAccessToken,
    setAccess: setAccessToken,
    getCsrf: getCsrfToken,
    setCsrf: setCsrfToken,
  },
  bearer: { protected: isBearerPath },
  csrf: {
    protected: (path) => isCsrfProtected(path),
    // saas-auth's surface is same-origin-cookie readable (`journix_csrf`) with a
    // GET /api/auth/csrf fallback — cookie-echo, header + body echo.
    mode: () => 'cookie-echo',
  },
  refresh: { path: '/api/auth/refresh' },
  onSessionExpired: signOut,
  generateRequestId,
});
